/* NADÏELLE — product page behaviour (variants, gallery, add to bag) */
(() => {
  if (customElements.get('ndl-product')) return;

  class NdlProduct extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-ndl-variants]');
      if (!raw) return;
      this.data = JSON.parse(raw.textContent);
      this.opts = [...this.querySelectorAll('.ndl-opt')];
      this.gallery = this.querySelector('.ndl-gallery');
      this.slides = [...this.querySelectorAll('.ndl-gallery__slide[data-index]')];
      this.thumbs = [...this.querySelectorAll('.ndl-thumb, .ndl-gallery__seg')];
      const first = this.slides.find(s => s.classList.contains('is-active'));
      this.current = first ? +first.dataset.index : 0;
      this.colorIndex = this.data.colorIndex ?? -1;
      this.grouped = this.slides.some(s => s.dataset.group);
      this.input = this.querySelector('[data-variant-input]');
      this.addBtn = this.querySelector('[data-add]');
      this.soldBtn = this.querySelector('[data-soldout]');
      this.priceEl = this.querySelector('[data-price]');
      this.msg = this.querySelector('[data-msg]');
      this.form = this.querySelector('form[data-ndl-form]');

      // initial selection from server-rendered state
      this.sel = this.opts.map(o => {
        const s = o.querySelector('.is-selected');
        return s ? s.dataset.value : null;
      });

      this.applyGroup();
      if (this.order.length && !this.order.includes(this.current)) this.showSlide(this.order[0]);

      this.addEventListener('click', e => this.onClick(e));
      this.initZoom();
      this.initGalleryNav();
      if (this.form) this.form.addEventListener('submit', e => this.onSubmit(e));
      this.update({ fromUser: false });
    }

    onClick(e) {
      const thumb = e.target.closest('[data-thumb]');
      if (thumb) { this.showSlide(+thumb.dataset.thumb); return; }
      const step = e.target.closest('[data-step]');
      if (step) { this.step(+step.dataset.step); return; }

      const btn = e.target.closest('.ndl-swatch, .ndl-size');
      if (!btn || !this.contains(btn)) return;
      const opt = btn.closest('.ndl-opt');
      const i = +opt.dataset.optionIndex;
      this.sel[i] = btn.dataset.value;
      opt.classList.remove('is-missing');
      this.setMsg('');
      this.update({ fromUser: true, changed: i });
    }

    /* Hover magnification at the photo's true size (100%: one photo pixel per screen pixel, never
       stretched). On first hover it loads the largest copy Shopify has, up to 2400px wide (Shopify
       never enlarges past the uploaded original), and pans with the cursor. How close the zoom gets
       therefore depends on the photo: a 1200px-wide photo in the 620px frame zooms about 1.9x.
       A photo barely bigger than the frame gets no zoom. */
    initZoom() {
      const main = this.querySelector('.ndl-gallery__main');
      if (!main || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const MIN_GAIN = 1.15; // a zoom smaller than this would hardly enlarge, so it stays off
      const active = () => main.querySelector('.ndl-gallery__slide.is-active');
      // no zoom layer or zoom cursor while the active photo is too small to enlarge in this frame
      const sync = () => {
        const slide = active();
        const z = slide && slide.querySelector('.ndl-zoom');
        main.classList.toggle('is-nozoom', !!(z && z.dataset.w && +z.dataset.w < main.clientWidth * MIN_GAIN));
      };
      const prep = slide => {
        const z = slide && slide.querySelector('.ndl-zoom');
        if (!z || z.dataset.loaded) return z;
        z.dataset.loaded = '1';
        const img = new Image();
        img.onload = () => {
          z.style.backgroundImage = `url("${img.src}")`;
          z.style.backgroundSize = `${img.naturalWidth}px auto`;
          z.dataset.w = img.naturalWidth;
          z.classList.add('is-ready');
          sync();
        };
        img.src = slide.dataset.zoom;
        return z;
      };
      const move = e => {
        main.classList.toggle('is-zooming', !e.target.closest('.ndl-gallery__nav'));
        const r = main.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, (e.clientX - r.left) / r.width * 100));
        const y = Math.min(100, Math.max(0, (e.clientY - r.top) / r.height * 100));
        const z = prep(active());
        if (z) z.style.backgroundPosition = `${x}% ${y}%`;
        sync();
      };
      main.addEventListener('pointerenter', e => { main.classList.add('is-zooming'); move(e); });
      main.addEventListener('pointermove', move);
      main.addEventListener('pointerleave', () => main.classList.remove('is-zooming'));
    }

    /* Photos grouped by colour. ndl-product.liquid tags every photo with its colour (data-group),
       worked out from the Media order; untagged photos are shared. Only the chosen colour's photos
       are shown, followed by the shared ones; arrows, swipe and the thumbnails go through them in
       that order. A colour with no photo of its own, or a product without colours, shows every
       photo in Media order. */
    applyGroup() {
      const color = this.colorIndex > -1 ? this.sel[this.colorIndex] : null;
      const on = this.grouped && color != null && this.slides.some(s => s.dataset.group === color);
      const own = on ? this.slides.filter(s => s.dataset.group === color) : this.slides;
      const shared = on ? this.slides.filter(s => !s.dataset.group) : [];
      this.order = [...own, ...shared].map(s => +s.dataset.index);
      const shown = new Set(this.order);
      const sharedIdx = new Set(shared.map(s => +s.dataset.index));
      this.slides.forEach(s => s.classList.toggle('is-off', !shown.has(+s.dataset.index)));
      this.thumbs.forEach(t => {
        const i = +t.dataset.thumb;
        const pos = this.order.indexOf(i);
        t.classList.toggle('is-off', pos === -1);
        t.classList.toggle('is-shared', sharedIdx.has(i)); // shared photos sit after the colour's own (CSS order)
        if (pos === -1) return;
        if (t.classList.contains('ndl-gallery__seg')) t.setAttribute('aria-label', `Image ${pos + 1} of ${this.order.length}`);
        else t.setAttribute('aria-label', (t.getAttribute('aria-label') || '').replace(/\d+$/, pos + 1));
      });
      if (this.gallery) this.gallery.classList.toggle('is-single', this.order.length < 2);
    }

    showSlide(index) {
      const slide = this.slides.find(s => +s.dataset.index === index);
      if (!slide) return;
      this.current = index;
      this.slides.forEach(s => s.classList.toggle('is-active', s === slide));
      this.thumbs.forEach(t => {
        const on = +t.dataset.thumb === index;
        t.classList.toggle('is-active', on);
        if (t.getAttribute('role') === 'tab') t.setAttribute('aria-selected', on);
      });
    }

    step(dir) {
      const n = this.order.length;
      if (n < 2) return;
      const p = Math.max(0, this.order.indexOf(this.current));
      this.showSlide(this.order[(p + dir + n) % n]);
    }

    /* keyboard arrows (when the gallery has focus) + swipe on touch screens */
    initGalleryNav() {
      const main = this.querySelector('.ndl-gallery__main');
      if (!main || this.slides.length < 2) return;
      main.tabIndex = 0;
      main.setAttribute('aria-label', 'Product images — use arrow keys to browse');
      main.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });
      let x0 = null, y0 = null;
      main.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      main.addEventListener('touchend', e => {
        if (x0 == null) return;
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
        x0 = null;
      });
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find(s => +s.dataset.mediaId === mediaId);
      if (slide && this.order.includes(+slide.dataset.index)) this.showSlide(+slide.dataset.index);
    }

    variantFor(sel) {
      // Product with no visible options (single "Default Title" variant)
      if (!this.opts.length) return this.data.variants[0] || null;
      if (sel.some(v => v == null)) return null;
      return this.data.variants.find(v => v.options.every((o, i) => o === sel[i])) || null;
    }

    /* is value `val` of option `i` purchasable, given the OTHER current choices? */
    isAvailable(i, val) {
      return this.data.variants.some(v =>
        v.available && v.options[i] === val &&
        v.options.every((o, j) => j === i || this.sel[j] == null || o === this.sel[j]));
    }

    update({ fromUser, changed }) {
      // buttons: selected + availability
      this.opts.forEach((opt, i) => {
        opt.querySelectorAll('.ndl-swatch, .ndl-size').forEach(b => {
          const on = b.dataset.value === this.sel[i];
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on);
          b.classList.toggle('is-unavailable', !this.isAvailable(i, b.dataset.value));
        });
        const label = opt.querySelector('[data-option-label]');
        if (label) label.textContent = this.sel[i] || '';
      });

      const variant = this.variantFor(this.sel);
      this.variant = variant;

      // gallery follows the colour: that colour's photos only, starting with its attached photo.
      // Any other non-size option jumps to the photo attached to the new choice, as before.
      if (fromUser && changed !== this.data.sizeIndex) {
        if (changed === this.colorIndex && this.grouped) {
          this.applyGroup();
          if (this.order.length) this.showSlide(this.order[0]);
        } else {
          const match = this.data.variants.find(v =>
            v.media && v.options[changed] === this.sel[changed]);
          if (match) this.showMedia(match.media);
        }
      }

      if (variant) {
        this.input.value = variant.id;
        this.priceEl.innerHTML =
          (variant.compare ? `<s>${variant.compare}</s>` : '') + `<span>${variant.price}</span>`;
        if (fromUser) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(null, '', url);
        }
      }

      const available = variant ? variant.available : true;
      if (this.addBtn) {
        this.addBtn.hidden = !available;
        this.addBtn.setAttribute('aria-disabled', variant ? 'false' : 'true');
      }
      if (this.soldBtn) this.soldBtn.hidden = available;
    }

    setMsg(t) { if (this.msg) this.msg.textContent = t; }

    async onSubmit(e) {
      if (!this.variant) {
        const missing = this.opts.find((o, i) => this.sel[i] == null);
        if (missing) {
          e.preventDefault();
          missing.classList.add('is-missing');
          this.setMsg(`Please select a ${missing.dataset.optionName.toLowerCase()}`);
        }
        return; // otherwise let the normal form post handle it
      }
      e.preventDefault();
      if (!this.variant.available || this.busy) return;
      this.busy = true;
      const root = window.Shopify?.routes?.root || '/';

      // Atelier 4.1.5 refreshes the bag drawer and the header bag count from Shopify's standard
      // CartLinesUpdateEvent (module '@shopify/events', mapped in snippets/scripts.liquid), not from
      // 'cart:update'. Same pattern as Atelier's own product form (assets/product-form.js): send the
      // event first, with a promise that is resolved once the new cart is known.
      const ev = await this.cartEvents();
      const deferred = ev ? ev.CartLinesUpdateEvent.createPromise() : null;
      if (deferred) {
        this.dispatchEvent(new ev.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(this.variant.id), quantity: 1 }],
          promise: deferred.promise
        }));
      }
      // ask for the bag drawer (and the cart page, if it is open) re-rendered with the new line
      const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map(n => n.dataset.sectionId).filter(Boolean);

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.variant.id, quantity: 1 }],
            ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {})
          })
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to bag');

        const cart = await (await fetch(`${root}cart.js`)).json();
        this.addBtn.classList.add('is-added');
        this.addBtn.textContent = 'Added to Bag ✓';
        setTimeout(() => {
          this.addBtn.classList.remove('is-added');
          this.addBtn.textContent = 'Add to Bag';
        }, 2000);

        if (deferred) {
          deferred.resolve({
            cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'product-form-component',
              sourceId: this.dataset.section,
              itemCount: 1,
              productId: this.dataset.productId,
              sections: body.sections,
              didError: false
            }
          });
        }
        document.querySelectorAll('[data-ndl-bag-count]').forEach(n => n.textContent = cart.item_count);
      } catch (err) {
        console.warn('[ndl] add to bag failed:', err);
        this.setMsg(err.message);
        if (deferred) {
          // let the drawer and bag count settle on the real cart; the drawer does not open on an error
          fetch(`${root}cart.js`).then(r => r.json()).then(cart => deferred.resolve({
            cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'product-form-component', sourceId: this.dataset.section,
              itemCount: 1, productId: this.dataset.productId, didError: true }
          })).catch(deferred.reject);
        }
      } finally {
        this.busy = false;
      }
    }

    // Shopify's standard events module, loaded through the theme's import map. Resolves to null if
    // it isn't available, so Add to Bag still works (the drawer then refreshes on the next page).
    cartEvents() {
      if (!this._cartEvents) {
        this._cartEvents = import('@shopify/events')
          .then(m => (m && m.CartLinesUpdateEvent && m.CartLinesUpdateEvent.createPromise ? m : null))
          .catch(() => null);
      }
      return this._cartEvents;
    }
  }

  customElements.define('ndl-product', NdlProduct);
})();
